import { get, isArray } from 'lodash-es';
import type { IOptionItem, ScalarType } from '@wyz/types';
import { Form, FormFeedback, FormGroup, Label } from '../index';
import { useController, useFormContext } from 'react-hook-form';
import { useState } from 'react';
import * as React from 'react';

export type CheckboxGroupOption<TValue extends ScalarType = string> =
  IOptionItem<TValue> & {
    labelNode?: React.ReactNode;
  };

type CheckboxGroupProps<
  TKeys extends string,
  TValue extends string | number,
> = {
  name: string;
  helperText?: string;
  label?: React.ReactNode;
  required?: boolean;
  readOnly?: boolean;
  options: CheckboxGroupOption<TValue>[];
  defaultValue?: unknown;
  translate: (key: TKeys) => string;
  isSearchable?: boolean;
  all?: {
    enabled?: boolean;
    label?: string;
    labelNode?: React.ReactNode;
  };
  /**
   * Only used to notify the parent about change, NOT to control the component
   */
  onChangeValue?: (
    checked: boolean,
    lastSelectedValue: TValue,
    allSelected: Array<TValue>,
  ) => void;
};

const ALL_CODENAME = 'ALL';

const stringCompare = (thisString: string, thatString: string) => {
  return (thisString || '')
    .toLowerCase()
    .includes((thatString || '').toLowerCase());
};

function CheckboxGroupField<
  TKeys extends string,
  TValue extends number | string,
>({
  name,
  label,
  options,
  helperText,
  required,
  translate,
  isSearchable,
  onChangeValue,
  all,
}: CheckboxGroupProps<TKeys, TValue>) {
  const methods = useFormContext();
  const errorMessage = get(methods.formState.errors, [
    name,
    'message',
  ]) as TKeys;
  const [searchQuery, setSearchQuery] = useState('');

  const { field } = useController({
    name,
    control: methods.control,
  });

  const allChecked = Boolean(
    all?.enabled &&
    field?.value &&
    isArray(field.value) &&
    field.value.length === options.length,
  );
  return (
    <FormGroup>
      {label && (
        <Label
          {...{ htmlFor: name }}
          className={`${required ? 'required' : ''}`}
        >
          {label}
        </Label>
      )}
      {isSearchable && (
        <Form.Control
          name='search'
          placeholder={translate('app.common.buttons.search' as TKeys)}
          className='form-checkbox-search'
          onChange={(event) => {
            setSearchQuery(event.target.value);
          }}
          size='sm'
        />
      )}
      {all?.enabled && all.label && options.length !== 0 && (
        <div>
          <Form.Check
            id={`${name}.${ALL_CODENAME}`}
            value={ALL_CODENAME}
            label={all.labelNode ?? all.label}
            type='checkbox'
            isInvalid={!!errorMessage}
            // the value of checked state is dynamically calculated
            checked={allChecked && options.length !== 0}
            // no need to handle this if there is no options available
            disabled={options.length === 0}
            data-cy={ALL_CODENAME}
            onChange={() => {
              if (allChecked) {
                field.onChange([]);
                onChangeValue?.(allChecked, ALL_CODENAME as TValue, []);
              } else {
                const allValues = options.map(({ value }) => value);
                field.onChange(allValues);
                onChangeValue?.(allChecked, ALL_CODENAME as TValue, allValues);
              }
            }}
          />
        </div>
      )}
      {options
        .filter((_option) =>
          searchQuery && isSearchable
            ? stringCompare(_option.label, searchQuery) ||
              stringCompare(_option.value.toString(), searchQuery)
            : true,
        )
        .map((item) => {
          const label = item.label || item.value;
          const optionId = `${name}.${String(label)}`;
          const isChecked = field.value?.includes(item.value) ?? false;

          return (
            <div key={`${name}-${label}`}>
              <Form.Check
                id={optionId}
                value={item.value}
                label={item.labelNode ?? label}
                type='checkbox'
                isInvalid={!!errorMessage}
                data-cy={optionId}
                checked={isChecked}
                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                  field.onChange(
                    isChecked
                      ? (field.value as TValue[]).filter(
                          (value) => item.value !== value,
                        )
                      : [...(field.value ?? []), item.value],
                  );
                  onChangeValue?.(
                    e.currentTarget.checked,
                    e.currentTarget.value as TValue,
                    field.value as TValue[],
                  );
                }}
              />
            </div>
          );
        })}
      {errorMessage ? (
        <FormFeedback invalid id={`${name}.error`}>
          {translate(errorMessage)}
        </FormFeedback>
      ) : helperText ? (
        <FormFeedback>{helperText}</FormFeedback>
      ) : null}
    </FormGroup>
  );
}

export default CheckboxGroupField;
